import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6ahri2sgkaik1und8g";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      body.append(key, String(value));
    });

    setStatus("submitting");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "提交失败，请稍后再试";

      const isSpam =
        typeof serverMsg === "string" &&
        (serverMsg.includes("spam") || serverMsg.includes("form data is spam"));

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(String(serverMsg));
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，提交失败，请稍后重试。");
      setStatus("error");
    }
  };

  const inputClass =
    "h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

  return (
    <form
      data-readdy-form
      id="mitao33-feedback-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
        <i className="ri-chat-smile-2-line text-primary-600" />
        意见建议
      </h3>
      <p className="mt-1 text-sm text-foreground-600">
        对我们的内容或功能有任何想法，欢迎留言告诉我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
            姓名
          </label>
          <input id="fb-name" name="name" type="text" required placeholder="请输入你的称呼" className={inputClass} />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
            邮箱
          </label>
          <input id="fb-email" name="email" type="email" required placeholder="name@example.com" className={inputClass} />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-topic" className="mb-1.5 block text-sm font-medium text-foreground-700">
          反馈类型
        </label>
        <select id="fb-topic" name="topic" defaultValue="内容建议" className={inputClass}>
          <option value="内容建议">内容建议</option>
          <option value="功能反馈">功能反馈</option>
          <option value="内容合作">内容合作</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
          留言内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请具体描述你的建议或问题（不超过 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm leading-relaxed text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
      </div>

      <input
        type="text"
        name="website_alt"
        className="form-guard-33"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-500">
          提交即表示你同意我们通过该邮箱与你取得联系。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              提交中...
            </>
          ) : (
            <>
              <i className="ri-send-plane-line" />
              提交反馈
            </>
          )}
        </button>
      </div>

      {status === "success" && (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
          <i className="ri-checkbox-circle-line mt-0.5" />
          感谢你的反馈，我们已经收到，会尽快与你联系。
        </div>
      )}
      {status === "error" && (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" />
          {formError}
        </div>
      )}
    </form>
  );
}